/**
 * Day templates — save a day's shape and apply it to another day.
 *
 * Applying is additive by default; "Replace day" clears first. Templates are
 * snapshots: editing one never rewrites days already planned from it.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { LayoutTemplate, Plus, Trash2, Check } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { formatMinute, formatDuration, formatDayLabel } from "@/lib/dates";

export function TemplatesDialog({
  open,
  onOpenChange,
  date,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string;
}) {
  const { toast } = useToast();
  const templates = useQuery(api.dashplanTemplates.list, open ? {} : "skip");
  const apply = useMutation(api.dashplanTemplates.apply);
  const remove = useMutation(api.dashplanTemplates.remove);
  const createFromDay = useMutation(api.dashplanTemplates.createFromDay);

  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const applyTemplate = async (id: Id<"dashplanTemplates">, replace: boolean) => {
    setBusy(true);
    try {
      const { applied } = await apply({ id, date, replace });
      toast({
        title: `Applied ${applied} block${applied === 1 ? "" : "s"}`,
        description: replace
          ? `Replaced everything on ${date}.`
          : `Added to ${date}.`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Could not apply template",
        description: String(error),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const saveCurrentDay = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await createFromDay({ date, name });
      setNewName("");
      toast({ title: `Saved “${name}”` });
    } catch (error) {
      toast({
        title: "Could not save template",
        description: String(error),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Day templates</DialogTitle>
          <DialogDescription>
            Reusable shapes for a day. Applying one writes its blocks onto{" "}
            {formatDayLabel(date).toLowerCase()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {templates === undefined && (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          )}

          {templates?.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">
              No templates yet. Plan a day you like, then save it below.
            </p>
          )}

          {templates?.map((template) => (
            <div
              key={template._id}
              className="rounded-lg border border-border p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <LayoutTemplate className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{template.name}</p>
                  {template.description && (
                    <p className="text-xs text-muted-foreground">
                      {template.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {template.blocks.length} block
                    {template.blocks.length === 1 ? "" : "s"} ·{" "}
                    {formatMinute(template.blocks[0]?.startMinute ?? 0)}–
                    {formatMinute(
                      Math.min(
                        1439,
                        (template.blocks.at(-1)?.startMinute ?? 0) +
                          (template.blocks.at(-1)?.durationMinutes ?? 0),
                      ),
                    )}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${template.name}`}
                  className="shrink-0 text-destructive hover:text-destructive"
                  onClick={() => remove({ id: template._id })}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <ul className="text-xs text-muted-foreground font-mono tabular-nums space-y-0.5 pl-6">
                {template.blocks.slice(0, 4).map((block, index) => (
                  <li key={index} className="truncate">
                    {formatMinute(block.startMinute)}{" "}
                    <span className="font-sans">{block.title}</span>{" "}
                    <span className="opacity-60">
                      {formatDuration(block.durationMinutes)}
                    </span>
                  </li>
                ))}
                {template.blocks.length > 4 && (
                  <li className="opacity-60">
                    +{template.blocks.length - 4} more
                  </li>
                )}
              </ul>

              <div className="flex gap-2 pl-6">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => applyTemplate(template._id, false)}
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Add to day
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => applyTemplate(template._id, true)}
                >
                  Replace day
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-col items-stretch gap-2 border-t border-border pt-4">
          <Label htmlFor="template-name" className="text-xs">
            Save {formatDayLabel(date).toLowerCase()} as a template
          </Label>
          <div className="flex gap-2">
            <Input
              id="template-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Standard work day"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveCurrentDay();
                }
              }}
            />
            <Button
              onClick={saveCurrentDay}
              disabled={busy || !newName.trim()}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
